/**
 * ContentNode component
 * トピックの具体的な内容を表示するノード
 */

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ClockIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContentNodeData } from '@/types/contentTypes';
import { getPhaseColor } from '@/types/phaseTypes';
import { useMapState } from '@/contexts/map';

/**
 * ContentNode component
 */
const ContentNode = ({ data, selected }: NodeProps) => {
  const nodeData = data as unknown as ContentNodeData;
  const { topicId, content, timestamp } = nodeData;
  const { mapData } = useMapState();
  
  // 関連するトピックノードを探す
  const topicNode = mapData?.nodes.find(node => node.id === topicId);
  
  // トピックのフェーズIDと色を取得
  const phaseId = topicNode?.phaseId || '';
  const phaseColor = getPhaseColor(phaseId, mapData?.phases || []);
  
  return (
    <div className={cn(
      "transition-transform duration-200", 
      selected && "scale-[1.02]"
    )}>
      {/* 上部と下部にハンドルを配置 - 接続不可能に設定 */}
      <Handle type="target" position={Position.Top} className="w-2 h-2" isConnectable={false} />
      <Handle type="source" position={Position.Bottom} className="w-2 h-2" isConnectable={false} />
      
      <Card 
        className={cn(
          "border shadow-sm bg-white",
          selected && "ring-2 ring-primary/50"
        )}
        style={{ width: `${nodeData.width || 220}px` }}
      >
        <CardContent className="p-3 space-y-1">
          {/* タイムスタンプバッジ */}
          <div className="flex justify-between items-center">
            <Badge 
              variant="outline" 
              className="px-2 py-0.5 flex items-center gap-1 text-xs font-normal"
              style={{ borderColor: phaseColor }}
            >
              <ClockIcon className="h-3 w-3 text-muted-foreground" />
              {timestamp}
            </Badge>
          </div>
          
          {/* 内容テキスト */}
          <p className="text-xs leading-tight">{content}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default memo(ContentNode);